import * as React from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const FILLED_DISABLED =
  'disabled:border-border-disabled disabled:bg-bg-disabled disabled:text-fg-disabled';

// For a disabled button whose own tooltip explains why; ReadOnlyHint-wrapped buttons must not use it.
const DISABLED_TOOLTIP_TARGET =
  'disabled:pointer-events-auto disabled:cursor-not-allowed';

const buttonVariants = cva(
  'ease-standard inline-flex items-center justify-center gap-2 rounded-sm border border-transparent font-semibold whitespace-nowrap transition-colors duration-(--dur-micro) disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: cn(
          'bg-primary text-primary-foreground hover:bg-navy-700',
          FILLED_DISABLED,
        ),
        destructive: cn(
          'bg-destructive text-destructive-foreground hover:bg-destructive-hover',
          FILLED_DISABLED,
        ),
        outline: cn(
          'border-border-2 bg-bg-surface text-fg-1 hover:border-navy-800',
          FILLED_DISABLED,
        ),
        ghost: 'text-fg-1 hover:bg-mist disabled:text-fg-disabled',
        link: 'text-fg-link underline-offset-4 hover:underline disabled:text-fg-disabled',
      },
      size: {
        default: 'text-fs-14 h-10 px-4',
        sm: 'text-fs-13 h-8 px-3',
        lg: 'text-fs-14 h-11 px-6',
        icon: 'size-10',
        'icon-md': 'size-9',
        'icon-sm': 'size-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {/* Always mounted: inserting it beside browser-translated text throws NotFoundError. */}
        <span
          aria-hidden="true"
          className={cn(
            'border-border-2 border-t-navy-800 size-3.5 shrink-0 animate-spin rounded-full border-2',
            !loading && 'hidden',
          )}
        />
        <Slottable>{children}</Slottable>
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants, DISABLED_TOOLTIP_TARGET };
